import React, { useState, useMemo } from 'react';
import { formatMoney } from '../../utils/format';
import {
  SPECIALTY_CONTRACT_SYNERGIES, LEVEL_BONUSES, VEHICLE_DRIVER_COMPATIBILITY,
  TRAINING_IMPACTS, XP_THRESHOLDS, getXpForLevel,
} from '../../data/impactTrees';
import { normalizeDriverLevel, getSeniorityLabel, SENIORITY_TIERS } from '../../utils/driverLevel';
import { localize } from '../../data/i18nData';
import {
  Trophy, TrendingUp, Star, Truck, FileText, Shield, Fuel,
  ChevronRight, ChevronDown, GraduationCap, Zap, Award, Target,
  ArrowRight, Lock, CheckCircle2, User, HelpCircle,
} from 'lucide-react';

function XpBar({ xp, xpToNext, level }) {
  const pct = Math.min(100, (xp / Math.max(1, xpToNext)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2.5 rounded-full bg-ink-700/40 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-slate-500 w-20 text-right font-medium">{xp} / {xpToNext} XP</span>
    </div>
  );
}

// Driver leaderboard card
function DriverRankCard({ driver: rawDriver, rank, isFirst }) {
  const driver = localize('driver', rawDriver);
  const _lvl = normalizeDriverLevel(driver.level);
  const levelData = LEVEL_BONUSES[_lvl] || LEVEL_BONUSES[1];
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
      isFirst ? 'border-warn/60 bg-warn/5' : 'border-ink-600 bg-ink-800'
    }`} data-testid={`rank-card-${driver.id}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        rank === 1 ? 'bg-warn/100 text-white' : rank === 2 ? 'bg-gray-400 text-white' : rank === 3 ? 'bg-amber-700 text-white' : 'bg-ink-700 text-slate-400'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-slate-100 truncate">{driver.name}</p>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-foreground" style={{ backgroundColor: levelData.color }}>
            Niv.{_lvl} · {getSeniorityLabel(_lvl)}
          </span>
        </div>
        <p className="text-[10px] text-slate-500">{driver.specialty}{driver.specialty2 ? ` / ${driver.specialty2}` : ''}</p>
        <XpBar xp={driver.xp || 0} xpToNext={driver.xpToNext || getXpForLevel(_lvl)} level={_lvl} />
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{driver.xp || 0} XP</p>
        <p className="text-[10px] text-slate-500">{(driver.trainings || []).length} formations</p>
      </div>
    </div>
  );
}

// Impact tree visualization for a driver
function DriverImpactTree({ driver, vehicles }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const _lvl = normalizeDriverLevel(driver.level);
  const levelData = LEVEL_BONUSES[_lvl] || LEVEL_BONUSES[1];
  const nextLevelData = LEVEL_BONUSES[Math.min(5, _lvl + 1)];

  // Get synergies for this driver's specialties
  const synergies = useMemo(() => {
    const results = [];
    [driver.specialty, driver.specialty2].filter(Boolean).forEach(spec => {
      const specSynergies = SPECIALTY_CONTRACT_SYNERGIES[spec];
      if (specSynergies) {
        Object.entries(specSynergies).forEach(([category, data]) => {
          if (!results.find(r => r.category === category && r.spec === spec)) {
            results.push({ spec, category, ...data });
          }
        });
      }
    });
    return results.sort((a, b) => b.revenueBonus - a.revenueBonus);
  }, [driver.specialty, driver.specialty2]);

  // Vehicle compatibility
  const vehicleCompat = useMemo(() => {
    return (vehicles || []).map(v => {
      const compat = VEHICLE_DRIVER_COMPATIBILITY[v.tier];
      if (!compat) return { vehicle: v, compatible: true, penalty: null, ideal: false };
      const hasLicense = (driver.licenses || ['B']).includes(compat.requiredLicense);
      const penalty = compat.levelPenalty[_lvl];
      const ideal = _lvl >= compat.idealLevel;
      return { vehicle: v, compatible: hasLicense, penalty: hasLicense ? penalty : null, ideal, requiredLicense: compat.requiredLicense };
    });
  }, [vehicles, _lvl, driver.licenses]);

  const toggle = (s) => setExpandedSection(expandedSection === s ? null : s);

  return (
    <div className="space-y-3" data-testid={`impact-tree-${driver.id}`}>
      {/* Level progression */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden">
        <button onClick={() => toggle('level')} className="w-full flex items-center gap-3 p-3 hover:bg-ink-700/40 transition-colors">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: levelData.color + '20' }}>
            <TrendingUp className="w-4 h-4" style={{ color: levelData.color }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-slate-200">Progression de niveau</p>
            <p className="text-[10px] text-slate-500">Niveau {_lvl} — {levelData.label} · {getSeniorityLabel(_lvl)}</p>
          </div>
          {expandedSection === 'level' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </button>

        {expandedSection === 'level' && (
          <div className="px-3 pb-3 space-y-2">
            {/* Current level bonuses */}
            <div className="bg-ink-700/30 rounded-lg p-3">
              <p className="text-xs font-semibold mb-2" style={{ color: levelData.color }}>Niv.{_lvl} — {levelData.label}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3 text-money" />
                  <span className="text-slate-400">Revenus : <strong className="text-money">+{(levelData.revenueBonus * 100)}%</strong></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Fuel className="w-3 h-3 text-accent" />
                  <span className="text-slate-400">Carburant : <strong className="text-accent">-{(levelData.fuelReduction * 100)}%</strong></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3 h-3 text-purple-500" />
                  <span className="text-slate-400">Accidents : <strong className="text-purple-400">-{(levelData.accidentReduction * 100)}%</strong></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="w-3 h-3 text-warn" />
                  <span className="text-slate-400">Satisfaction : <strong className="text-warn">+{levelData.satisfactionBonus}</strong></span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-ink-600">
                <p className="text-[10px] text-slate-500 mb-1">Débloqué :</p>
                {levelData.unlocks.map((u, i) => (
                  <p key={`unlock-${u}`} className="text-[10px] text-money flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {u}</p>
                ))}
              </div>
            </div>

            {/* Next level preview */}
            {_lvl < 5 && nextLevelData && (
              <div className="border border-dashed border-ink-500 rounded-lg p-3 opacity-75">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Prochain : Niv.{_lvl + 1} — {nextLevelData.label}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">
                  Encore {Math.max(0, getXpForLevel(_lvl) - (driver.xp || 0))} XP nécessaires
                </p>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {nextLevelData.unlocks.map((u, i) => (
                    <span key={`next-unlock-${u}`} className="text-[10px] px-1.5 py-0.5 rounded bg-ink-700/40 text-slate-500">{u}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contract synergies */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden">
        <button onClick={() => toggle('contracts')} className="w-full flex items-center gap-3 p-3 hover:bg-ink-700/40 transition-colors">
          <div className="w-8 h-8 rounded-lg bg-money/10 flex items-center justify-center">
            <FileText className="w-4 h-4 text-money" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-slate-200">Synergies contrats</p>
            <p className="text-[10px] text-slate-500">{synergies.length} bonus actifs</p>
          </div>
          {expandedSection === 'contracts' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </button>

        {expandedSection === 'contracts' && (
          <div className="px-3 pb-3 space-y-1.5">
            {synergies.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-2">Aucune synergie active</p>
            ) : (
              synergies.map((s, i) => (
                <div key={`syn-${s.spec}-${s.category}`} className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                  s.tier === 'excellent' ? 'bg-money/10' : s.tier === 'bon' ? 'bg-accent/10' : 'bg-ink-700/30'
                }`}>
                  <div className="flex items-center gap-2">
                    <ArrowRight className={`w-3 h-3 ${
                      s.tier === 'excellent' ? 'text-money' : s.tier === 'bon' ? 'text-accent' : 'text-slate-500'
                    }`} />
                    <span className="text-slate-300">{s.spec} → {s.category}</span>
                  </div>
                  <span className={`font-bold ${
                    s.tier === 'excellent' ? 'text-money' : s.tier === 'bon' ? 'text-accent' : 'text-slate-500'
                  }`}>{s.label}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Vehicle compatibility */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden">
        <button onClick={() => toggle('vehicles')} className="w-full flex items-center gap-3 p-3 hover:bg-ink-700/40 transition-colors">
          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <Truck className="w-4 h-4 text-accent" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-slate-200">Compatibilité véhicules</p>
            <p className="text-[10px] text-slate-500">{vehicleCompat.filter(c => c.compatible).length}/{vehicleCompat.length} compatibles</p>
          </div>
          {expandedSection === 'vehicles' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </button>

        {expandedSection === 'vehicles' && (
          <div className="px-3 pb-3 space-y-1.5">
            {vehicleCompat.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-2">Aucun véhicule dans la flotte</p>
            ) : (
              vehicleCompat.map((c, i) => (
                <div key={`compat-${c.vehicle.id}`} className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                  !c.compatible ? 'bg-danger/10' : c.ideal ? 'bg-money/10' : c.penalty ? 'bg-warn/10' : 'bg-ink-700/30'
                }`}>
                  <div className="flex items-center gap-2">
                    <Truck className={`w-3 h-3 ${!c.compatible ? 'text-danger' : c.ideal ? 'text-money' : 'text-warn'}`} />
                    <span className="text-slate-300">{c.vehicle.brand} {c.vehicle.model}</span>
                    <span className="text-[10px] px-1 py-0.5 rounded bg-ink-800 text-slate-500">T{c.vehicle.tier}</span>
                  </div>
                  {!c.compatible ? (
                    <span className="text-danger font-medium flex items-center gap-0.5"><Lock className="w-3 h-3" /> Permis {c.requiredLicense} requis</span>
                  ) : c.ideal ? (
                    <span className="text-money font-medium flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> Optimal</span>
                  ) : c.penalty ? (
                    <span className="text-warn font-medium">{c.penalty.label}</span>
                  ) : (
                    <span className="text-slate-500">Compatible</span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Training impact */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden">
        <button onClick={() => toggle('trainings')} className="w-full flex items-center gap-3 p-3 hover:bg-ink-700/40 transition-colors">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <GraduationCap className="w-4 h-4 text-purple-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-slate-200">Impact des formations</p>
            <p className="text-[10px] text-slate-500">{(driver.trainings || []).length} suivie(s)</p>
          </div>
          {expandedSection === 'trainings' ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </button>

        {expandedSection === 'trainings' && (
          <div className="px-3 pb-3 space-y-2">
            {Object.entries(TRAINING_IMPACTS).map(([id, training]) => {
              const done = (driver.trainings || []).some(t => t.type === id);
              return (
                <div key={id} className={`p-2.5 rounded-lg border ${done ? 'border-purple-200 bg-purple-500/10/50' : 'border-ink-600 opacity-60'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {done ? <CheckCircle2 className="w-3.5 h-3.5 text-purple-500" /> : <Lock className="w-3.5 h-3.5 text-slate-600" />}
                    <p className={`text-xs font-semibold ${done ? 'text-purple-400' : 'text-slate-500'}`}>{training.name}</p>
                  </div>
                  <div className="space-y-0.5 ml-5.5">
                    {training.impacts.map((impact, j) => (
                      <p key={`impact-${id}-${impact.label}`} className="text-[10px] text-slate-400 flex items-center gap-1">
                        <ArrowRight className="w-2.5 h-2.5 text-slate-500" /> {impact.label}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function HRDashboard({ drivers, vehicles, totalDays }) {
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Sorted by XP
  const ranked = useMemo(() =>
    [...drivers].sort((a, b) => (b.xp || 0) - (a.xp || 0)),
    [drivers]
  );

  // Stats
  // 17/06/2026 — Bug user : "Niveau moyen NaN" car certains chauffeurs
  // avaient level = 'Junior' (string) → reducer produisait '0JuniorSenior'.
  // On normalise via normalizeDriverLevel pour garantir un entier 1-5.
  const avgLevel = drivers.length > 0
    ? (drivers.reduce((s, d) => s + normalizeDriverLevel(d.level), 0) / drivers.length).toFixed(1)
    : '—';
  const totalXp = drivers.reduce((s, d) => s + (d.xp || 0), 0);
  const totalTrainings = drivers.reduce((s, d) => s + (d.trainings || []).length, 0);
  const bestDriver = ranked[0];

  return (
    <div className="space-y-4" data-testid="hr-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div
          className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center relative group"
          data-testid="hr-stat-avg-level"
        >
          <p className="text-[10px] text-slate-500 flex items-center justify-center gap-1">
            Niveau moyen
            <HelpCircle className="w-3 h-3 text-slate-500 cursor-help" aria-label="Aide niveaux" />
          </p>
          <p className="text-lg font-bold text-foreground">{avgLevel}</p>
          {/* Tooltip pédagogique (17/06/2026 — retour user "quelle diff
              entre Junior/Confirmé/Sénior ?") */}
          <div
            className="absolute z-20 left-1/2 -translate-x-1/2 top-full mt-2 w-64 hidden group-hover:block p-2.5 rounded-md bg-ink-900 border border-ink-600 text-left shadow-xl"
            role="tooltip"
            data-testid="seniority-tooltip"
          >
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Paliers de carrière</p>
            <ul className="space-y-1">
              {SENIORITY_TIERS.map(tier => (
                <li key={tier.label} className="text-[10px] text-slate-300 leading-snug">
                  <span className="font-bold text-accent">{tier.label}</span>
                  <span className="text-slate-500"> (Niv. {tier.range}) </span>
                  — {tier.desc}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-slate-500 mt-1.5 italic">Système 1-5 progressif. La formation et l&apos;XP font monter de palier.</p>
          </div>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-[10px] text-slate-500">XP total équipe</p>
          <p className="text-lg font-bold text-indigo-600">{totalXp}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-[10px] text-slate-500">Formations suivies</p>
          <p className="text-lg font-bold text-purple-400">{totalTrainings}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-warn/40 p-3 text-center">
          <p className="text-[10px] text-slate-500">Chauffeur du mois</p>
          <p className="text-sm font-bold text-warn truncate">{bestDriver?.name || '—'}</p>
        </div>
      </div>

      {/* Two-column layout: ranking + impact tree */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Ranking */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-warn" /> Classement XP
          </h3>
          {ranked.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Aucun chauffeur recruté</p>
          ) : (
            <div className="space-y-2">
              {ranked.map((d, i) => (
                <div key={d.id} className="cursor-pointer" onClick={() => setSelectedDriver(d.id === selectedDriver ? null : d.id)}>
                  <DriverRankCard driver={d} rank={i + 1} isFirst={i === 0} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Impact Tree */}
        <div>
          <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-indigo-500" /> Arbre d&apos;impact
            {selectedDriver && (
              <span className="text-[10px] text-slate-500 font-normal">
                — {drivers.find(d => d.id === selectedDriver)?.name}
              </span>
            )}
          </h3>
          {selectedDriver ? (
            <DriverImpactTree
              driver={drivers.find(d => d.id === selectedDriver)}
              vehicles={vehicles}
            />
          ) : (
            <div className="bg-ink-700/30 rounded-lg border border-dashed border-ink-500 p-8 text-center">
              <User className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Sélectionnez un chauffeur dans le classement pour voir son arbre d&apos;impact</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
